import api, { deviceService } from './api';

// Lightweight wrapper used by admin pages to perform cross-tenant operations.
const apiService = {
  // Fetch all devices across tenants (ADMIN endpoint)
  async getAllDevices(params = {}) {
    const response = await api.get('/admin/devices', { params });
    // backend returns { success: true, data: [...], pagination: {...} }
    if (response.data && response.data.data) return response.data.data;
    return response.data;
  },

  // Use ADMIN endpoint for status change
  async updateDeviceStatus(deviceId, status, reason = '') {
    const response = await api.post(`/admin/devices/${deviceId}/change-status`, {
      status,
      reason
    });
    return response.data;
  },

  // Use ADMIN endpoint for device deletion
  async deleteDevice(deviceId, reason = '') {
    const response = await api.delete(`/admin/devices/${deviceId}`, {
      data: { reason }
    });
    return response.data;
  },

  // Use ADMIN endpoint for updating device notes
  async updateDeviceNotes(deviceId, notes) {
    const response = await api.put(`/admin/devices/${deviceId}`, { notes });
    return response.data;
  },

  // Use ADMIN endpoint for full device update
  async updateDevice(deviceId, deviceData) {
    const response = await api.put(`/admin/devices/${deviceId}`, deviceData);
    return response.data;
  },

  // WebSocket Management (ADMIN only)
  async checkDeviceConnection(deviceId) {
    return deviceService.checkDeviceConnection(deviceId);
  },

  async sendDisplayMessage(deviceId, messageData) {
    return deviceService.sendDisplayMessage(deviceId, messageData);
  },

  async sendWebSocketCommand(deviceId, commandData) {
    return deviceService.sendWebSocketCommand(deviceId, commandData);
  },

  // Remote device reboot via WebSocket (uses generic command endpoint)
  async rebootDevice(deviceId, reason = '') {
    return this.sendWebSocketCommand(deviceId, {
      event: 'device:remote_reboot',
      data: { reason }
    });
  },

  // ============================================================================
  // DEVICE HEALTH MONITORING
  // ============================================================================

  /**
   * Get detailed health status for a specific device
   * @param {string} deviceId - Device ID
   * @returns {Promise<Object>} Health details with WS/API/Reader status
   */
  async getDeviceHealth(deviceId) {
    const response = await api.get(`/admin/devices/${deviceId}/health`);
    return response.data;
  },

  /**
   * Get health overview for all devices in a tenant
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} Overview with devices list and summary stats
   */
  async getTenantDevicesHealth(tenantId) {
    const response = await api.get(`/admin/devices/tenant/${tenantId}/health-overview`);
    return response.data;
  },

  /**
   * Get health status for all devices (admin view)
   * Fetches health for all devices shown in AllDevices page
   * @param {Array<string>} deviceIds - Array of device IDs to fetch health for
   * @returns {Promise<Object>} Map of deviceId -> health status
   */
  async getBulkDevicesHealth(deviceIds) {
    if (!Array.isArray(deviceIds) || deviceIds.length === 0) return {};
    const response = await api.post('/admin/devices/health/bulk', { deviceIds });
    return response.data;
  },

  // ── Venue Categories ──────────────────────────────────────────────────────
  async getVenueCategories() {
    const response = await api.get('/admin/venue-categories');
    return response.data;
  },
  async getVenueCategoryAssociationImpactPreview(id, venueIds = []) {
    const response = await api.post(`/admin/venue-categories/${id}/association-impact-preview`, {
      venueIds,
    });
    return response.data;
  },
  async createVenueCategory(data) {
    const response = await api.post('/admin/venue-categories', data);
    return response.data;
  },
  async updateVenueCategory(id, data) {
    const response = await api.put(`/admin/venue-categories/${id}`, data);
    return response.data;
  },
  async deleteVenueCategory(id) {
    const response = await api.delete(`/admin/venue-categories/${id}`);
    return response.data;
  },

  // ── Profile Roles ─────────────────────────────────────────────────────────
  async getProfileRoles() {
    const response = await api.get('/admin/profile-roles');
    return response.data;
  },
  async createProfileRole(data) {
    const response = await api.post('/admin/profile-roles', data);
    return response.data;
  },
  async updateProfileRole(id, data) {
    const response = await api.put(`/admin/profile-roles/${id}`, data);
    return response.data;
  },

  // ── Media Assets ──────────────────────────────────────────────────────────
  async getMediaAssets(params = {}) {
    const response = await api.get('/admin/media', { params });
    return response.data;
  },
  async uploadMediaAsset(formData) {
    const response = await api.post('/admin/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  getMediaPreviewUrl(id) {
    return `${api.defaults.baseURL}/admin/media/${id}/preview`;
  },
  async deleteMediaAsset(id) {
    const response = await api.delete(`/admin/media/${id}`);
    return response.data;
  },
  async getMediaFolders() {
    const response = await api.get('/admin/media/folders');
    return response.data;
  },
  async createMediaFolder(path) {
    const response = await api.post('/admin/media/folders', { path });
    return response.data;
  },
  async deleteMediaFolder(path) {
    const response = await api.delete(`/admin/media/folders/${encodeURIComponent(path)}`);
    return response.data;
  },
  async moveMediaAsset(id, folder) {
    const response = await api.patch(`/admin/media/${id}/folder`, { folder: folder ?? null });
    return response.data;
  },

  // ── Remote Device Config ─────────────────────────────────────────────────
  async getRemoteConfigs(params = {}) {
    const response = await api.get('/admin/remote-device-config', { params });
    return response.data;
  },
  async getRemoteConfig(id) {
    const response = await api.get(`/admin/remote-device-config/${id}`);
    return response.data;
  },
  async createRemoteConfig(data) {
    const response = await api.post('/admin/remote-device-config', data);
    return response.data;
  },
  async updateRemoteConfig(id, data) {
    const response = await api.put(`/admin/remote-device-config/${id}`, data);
    return response.data;
  },
  async getRemoteConfigImpactedDevices(id, { scope, scopeEntityId, namespace } = {}) {
    const params = {};
    if (scope !== undefined) params.scope = scope;
    if (scopeEntityId !== undefined) params.scopeEntityId = scopeEntityId ?? '';
    if (namespace !== undefined) params.namespace = namespace;
    const response = await api.get(`/admin/remote-device-config/${id}/impacted-devices`, { params });
    return response.data;
  },
  async publishRemoteConfig(id, notifyWs = true) {
    const response = await api.post(`/admin/remote-device-config/${id}/publish`, { notifyWs });
    return response.data;
  },
  async rollbackRemoteConfig(id, versionId, notifyWs = true) {
    const response = await api.post(`/admin/remote-device-config/${id}/rollback`, { versionId, notifyWs });
    return response.data;
  },
  async getRemoteConfigVersions(id) {
    const response = await api.get(`/admin/remote-device-config/${id}/versions`);
    return response.data;
  },
  async archiveRemoteConfig(id, notifyWs = true) {
    const response = await api.post(`/admin/remote-device-config/${id}/archive`, { notifyWs });
    return response.data;
  },
  async deleteRemoteConfig(id) {
    const response = await api.delete(`/admin/remote-device-config/${id}`);
    return response.data;
  },

  // ─── Device Activation Support ───────────────────────────────────────────
  async searchDeviceActivation(params = {}) {
    const response = await api.get('/admin/device-activation/search', { params });
    return response.data;
  },
  async getDeviceActivationStatus(deviceId) {
    const response = await api.get(`/admin/device-activation/${deviceId}/status`);
    return response.data;
  },
  async regenerateDeviceActivation(deviceId, reason, deliveryMode = 'show_once') {
    const response = await api.post(`/admin/device-activation/${deviceId}/regenerate`, { reason, deliveryMode });
    return response.data;
  },
  async resetDeviceActivationLockout(deviceId, reason) {
    const response = await api.post(`/admin/device-activation/${deviceId}/reset-lockout`, { reason });
    return response.data;
  },
  async getDeviceActivationHistory(deviceId, limit = 50) {
    const response = await api.get(`/admin/device-activation/${deviceId}/history`, { params: { limit } });
    return response.data;
  },
  async sendDeviceActivationInstructions(deviceId, { deliveryMode, reason, recipientEmail, pin }) {
    const response = await api.post(`/admin/device-activation/${deviceId}/send-instructions`, {
      deliveryMode,
      reason,
      recipientEmail,
      pin,
    });
    return response.data;
  },
};

export default apiService;
